import { MerklCampaign, RewardsToken } from '@/hooks/other/useMerklDappCampaigns';
import { CombinedCampaign, CombinedRewardToken } from '../types/index';
import { endOfDay, isWithinInterval, startOfDay } from 'date-fns';

/**
 * Combines multiple campaigns by pool address.
 * @param campaigns - List of MerklCampaign objects to combine.
 * @returns An array of CombinedCampaign objects grouped by pool address.
 */
export const combineCampaignsByPool = (campaigns: MerklCampaign[]): CombinedCampaign[] => {
  const combinedCampaignsMap = campaigns.reduce<Record<string, MerklCampaign>>((acc, campaign) => {
    const { pool_address, reward_tokens, apr, pool_tvl } = campaign;

    if (!acc[pool_address]) {
      acc[pool_address] = { ...campaign, reward_tokens: [], hasMoreThanOneCampaign: false };
    }

    const existingCampaign = acc[pool_address];

    const rewardTokenMap = existingCampaign.reward_tokens.reduce<{ [address: string]: RewardsToken }>(
      (tokenAcc, token) => {
        const address = token.reward_token_address;
        if (!tokenAcc[address]) {
          tokenAcc[address] = { ...token };
        } else {
          tokenAcc[address].reward_token_amount += token.reward_token_amount;
        }
        return tokenAcc;
      },
      {}
    );

    reward_tokens.forEach((token) => {
      const address = token.reward_token_address;
      if (!rewardTokenMap[address]) {
        rewardTokenMap[address] = { ...token };
      } else {
        rewardTokenMap[address].reward_token_amount += token.reward_token_amount;
      }
    });

    acc[pool_address] = {
      ...existingCampaign,
      reward_tokens: Object.values(rewardTokenMap),
      apr: Math.max(existingCampaign.apr, apr),
      pool_tvl: existingCampaign.pool_tvl + pool_tvl,
      hasMoreThanOneCampaign: true,
    };

    return acc;
  }, {});

  return Object.values(combinedCampaignsMap)
    .map((campaign) => {
      const totalRewardAmount = campaign.reward_tokens.reduce((sum, token) => sum + token.reward_token_amount, 0);

      const rewardTokensWithWeights = campaign.reward_tokens.map((token) => ({
        ...token,
        reward_token_weight: totalRewardAmount > 0 ? (token.reward_token_amount / totalRewardAmount) * 100 : 0,
      }));

      const startDate = new Date(campaign.start_date);
      const endDate = new Date(campaign.end_date);
      const today = new Date();

      const isInRange = isWithinInterval(today, {
        start: startOfDay(startDate),
        end: endOfDay(endDate),
      });

      const campaign_url =
        campaign.campaign_type === 2
          ? `https://merkl.angle.money/mode/pool/2/${campaign.pool_address}`
          : `https://merkl.angle.money/mode/hold/1/${campaign.pool_address}`;

      return {
        ...campaign,
        reward_tokens: rewardTokensWithWeights,
        is_in_range: isInRange,
        campaign_url,
      };
    })
    .sort((a, b) => b.apr - a.apr);
};
